import { format } from "date-fns";
import { Tables } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Mail, Phone, ExternalLink, FileText, User, Calendar, Clock, MapPin, Building2 } from "lucide-react";

type JobApplication = Tables<"job_applications"> & {
  job_postings: { title: string; department: string } | null;
};

interface ApplicationDetailsDialogProps {
  application: JobApplication;
  isOpen: boolean;
  onClose: () => void;
}

const statusColors = {
  submitted: "default",
  reviewing: "secondary",
  interviewing: "outline",
  rejected: "destructive",
  accepted: "default",
} as const;

export const ApplicationDetailsDialog = ({ application, isOpen, onClose }: ApplicationDetailsDialogProps) => {
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);

  useEffect(() => {
    const getResumeUrl = async () => {
      try {
        const { data, error } = await supabase.storage
          .from('resumes')
          .list('', {
            search: `${application.id}_`
          });

        if (error) {
          console.error('Error fetching resume:', error);
          return;
        }

        const resumeFile = data?.find(file => 
          file.name.startsWith(`${application.id}_`) && 
          file.name.toLowerCase().includes('resume')
        );

        if (resumeFile) {
          const { data: urlData } = supabase.storage
            .from('resumes')
            .getPublicUrl(resumeFile.name);
          
          setResumeUrl(urlData.publicUrl);
        }
      } catch (error) {
        console.error('Error getting resume URL:', error);
      }
    };

    if (isOpen) {
      getResumeUrl();
    }
  }, [application.id, isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {application.first_name} {application.last_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Contact Information */}
          <div className="bg-card border rounded-lg p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <User className="h-4 w-4" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium text-muted-foreground">Full Name</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm">{application.first_name} {application.last_name}</span>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-muted-foreground">Email Address</label>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{application.email}</span>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-muted-foreground">Phone Number</label>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{application.phone || "Not provided"}</span>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-muted-foreground">Application Status</label>
                <div>
                  <Badge variant={statusColors[application.status as keyof typeof statusColors] || "default"}>
                    {application.status || "Submitted"}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Job Information */}
          <div className="bg-card border rounded-lg p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Applied Position
            </h3>
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-sm font-medium text-muted-foreground">Job Title</label>
                <p className="text-sm font-medium">{application.job_postings?.title || "Unknown Position"}</p>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-muted-foreground">Department</label>
                <p className="text-sm">{application.job_postings?.department || "Unknown Department"}</p>
              </div>
            </div>
          </div>

          {/* Documents & Links */}
          <div className="bg-card border rounded-lg p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Documents & Links
            </h3>
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-sm font-medium text-muted-foreground">Resume</label>
                <div>
                  {resumeUrl ? (
                    <Button variant="outline" size="sm" asChild>
                      <a href={resumeUrl} target="_blank" rel="noopener noreferrer">
                        <FileText className="h-4 w-4 mr-2" />
                        View Resume
                      </a>
                    </Button>
                  ) : (
                    <span className="text-sm text-muted-foreground">Not provided</span>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-muted-foreground">LinkedIn Profile</label>
                <div>
                  {application.linkedin_url ? (
                    <Button variant="outline" size="sm" asChild>
                      <a href={application.linkedin_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View LinkedIn
                      </a>
                    </Button>
                  ) : (
                    <span className="text-sm text-muted-foreground">Not provided</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Cover Letter */}
          <div className="bg-card border rounded-lg p-4">
            <h3 className="font-semibold mb-3">Cover Letter</h3>
            <div className="space-y-1">
              {application.cover_letter ? (
                <div className="bg-muted p-3 rounded-lg max-h-64 overflow-y-auto">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {application.cover_letter}
                  </p>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">Not provided</span>
              )}
            </div>
          </div>

          {/* Additional Information */}
          <div className="bg-card border rounded-lg p-4">
            <h3 className="font-semibold mb-3">Additional Information</h3>
            <div className="space-y-1">
              {application.additional_info ? (
                <div className="bg-muted p-3 rounded-lg max-h-64 overflow-y-auto">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {application.additional_info}
                  </p>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">Not provided</span>
              )}
            </div>
          </div>

          {/* Application Timeline */}
          <div className="bg-card border rounded-lg p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Application Timeline
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium text-muted-foreground">Date Applied</label>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {application.applied_at ? format(new Date(application.applied_at), "PPP") : "Unknown"}
                  </span>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-muted-foreground">Time Applied</label>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {application.applied_at ? format(new Date(application.applied_at), "p") : "Unknown"}
                  </span>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-muted-foreground">Record Created</label>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {application.created_at ? format(new Date(application.created_at), "PPP 'at' p") : "Unknown"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
